import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion, Reorder } from "framer-motion";
import { Plus, Trash2, GripVertical, ClipboardCheck } from "lucide-react";

const createChecklistItem = (order = 1, overrides = {}) => ({
  id: overrides.id ?? `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  description: overrides.description ?? "",
  displayOrder: overrides.displayOrder ?? order,
  isChecklist: overrides.isChecklist ?? true,
});

const normalizeChecklistOrder = (items = []) =>
  items.map((item, index) => ({
    ...item,
    displayOrder: index + 1,
  }));

export default function TaskTemplateModal({
  open,
  mode = "create",
  initialData = null,
  loading = false,
  onClose,
  onSubmit,
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [checklistItems, setChecklistItems] = useState([createChecklistItem(1)]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;

    if (initialData) {
      setName(initialData.name || "");
      setDescription(initialData.description || "");
      setChecklistItems(
        initialData.checklistItems?.length
          ? normalizeChecklistOrder(
              [...initialData.checklistItems]
                .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                .map((item, index) =>
                  createChecklistItem(index + 1, {
                    id: item.id ?? `existing-${index + 1}`,
                    description: item.description || "",
                    isChecklist: !!item.isChecklist,
                  })
                )
            )
          : [createChecklistItem(1)]
      );
    } else {
      setName("");
      setDescription("");
      setChecklistItems([createChecklistItem(1)]);
    }

    setErrors({});
  }, [open, initialData]);

  const modalTitle = useMemo(
    () => (mode === "edit" ? "Edit task template" : "Create task template"),
    [mode]
  );

  const addChecklistItem = () => {
    setChecklistItems((prev) =>
      normalizeChecklistOrder([...prev, createChecklistItem(prev.length + 1)])
    );
  };

  const removeChecklistItem = (index) => {
    setChecklistItems((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? normalizeChecklistOrder(next) : [createChecklistItem(1)];
    });
  };

  const updateChecklistItem = (index, key, value) => {
    setChecklistItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [key]: value } : item))
    );
  };

  const handleReorder = (nextOrder) => {
    setChecklistItems(normalizeChecklistOrder(nextOrder));
  };

  const validate = () => {
    const nextErrors = {};

    if (!name.trim()) {
      nextErrors.name = "Template name is required";
    }

    const invalidItems = checklistItems.some((item) => !item.description?.trim());

    if (invalidItems) {
      nextErrors.checklistItems = "Each checklist row must have a description";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const payload = {
      ...(mode === "edit" && initialData?.id ? { templateId: initialData.id } : {}),
      name: name.trim(),
      description: description.trim(),
      checklistItems: checklistItems.map((item, index) => ({
        description: item.description.trim(),
        displayOrder: index + 1,
        isChecklist: !!item.isChecklist,
      })),
    };

    onSubmit(payload);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950"
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
          >
            <div className="border-b border-slate-200 px-6 py-5 dark:border-slate-800">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                    {modalTitle}
                  </h2>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Define a reusable template and its ordered checklist structure.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="space-y-5 lg:col-span-1">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-900">
                    <div className="space-y-4">
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                          Template name
                        </label>
                        <input
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="e.g. Technician Site Visit"
                          className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none ring-0 transition placeholder:text-slate-400 focus:border-slate-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                        />
                        {errors.name && (
                          <p className="mt-2 text-xs font-medium text-red-500">
                            {errors.name}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                          Description
                        </label>
                        <textarea
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          rows={6}
                          placeholder="Describe what this template is for"
                          className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 lg:col-span-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                        Checklist items
                      </h3>
                      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        Drag and drop steps into the order they should appear.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={addChecklistItem}
                      className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
                    >
                      <Plus className="h-4 w-4" />
                      Add item
                    </button>
                  </div>

                  {errors.checklistItems && (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-400">
                      {errors.checklistItems}
                    </div>
                  )}

                  <Reorder.Group
                    axis="y"
                    values={checklistItems}
                    onReorder={handleReorder}
                    className="space-y-3"
                  >
                    {checklistItems.map((item, index) => (
                      <Reorder.Item
                        key={item.id}
                        value={item}
                        dragListener={true}
                        className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
                      >
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
                          <div className="flex items-center gap-3 pt-1">
                            <div className="cursor-grab rounded-xl bg-slate-100 p-2 text-slate-500 active:cursor-grabbing dark:bg-slate-800 dark:text-slate-300">
                              <GripVertical className="h-4 w-4" />
                            </div>

                            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900 text-sm font-semibold text-white dark:bg-white dark:text-slate-900">
                              {index + 1}
                            </div>
                          </div>

                          <div className="grid flex-1 grid-cols-1 gap-4 lg:grid-cols-[1fr_180px_auto]">
                            <div>
                              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                                Description
                              </label>
                              <input
                                value={item.description}
                                onChange={(e) =>
                                  updateChecklistItem(index, "description", e.target.value)
                                }
                                placeholder="Enter checklist instruction"
                                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                              />
                            </div>

                            <div>
                              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                                Type
                              </label>
                              <button
                                type="button"
                                onClick={() =>
                                  updateChecklistItem(index, "isChecklist", !item.isChecklist)
                                }
                                className={`flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium transition ${
                                  item.isChecklist
                                    ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-400"
                                    : "border-slate-300 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300"
                                }`}
                              >
                                <ClipboardCheck className="h-4 w-4" />
                                {item.isChecklist ? "Checklist item" : "Instruction only"}
                              </button>
                            </div>

                            <div className="flex items-end">
                              <button
                                type="button"
                                onClick={() => removeChecklistItem(index)}
                                className="inline-flex h-[46px] w-[46px] items-center justify-center rounded-xl border border-red-200 bg-red-50 text-red-600 transition hover:bg-red-100 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-400 dark:hover:bg-red-950/60"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </Reorder.Item>
                    ))}
                  </Reorder.Group>
                </div>
              </div>
            </div>

            <div className="border-t border-slate-200 px-6 py-5 dark:border-slate-800">
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
                >
                  {loading
                    ? "Saving..."
                    : mode === "edit"
                    ? "Save changes"
                    : "Create template"}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}